import Big from "big.js";
/**
 * This class defines an Amount for arbitrary-precision decimal arithmetic.
 *
 * It inherits methods from [big.js](http://mikemcl.github.io/big.js/) library
 *
 * @public
 */
export class Amount {
    /**
     * The Amount constructor.
     *
     * @param n - The number, string, or Amount to initialize with
     */
    constructor(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            this.big = new Big(n);
        }
        else if (n instanceof Amount) {
            this.big = new Big(n.big);
        }
        else if (n.toString) {
            this.big = new Big(n.toString());
        }
        else {
            this.big = new Big(+n);
        }
    }
    /**
     * Returns an absolute Amount.
     *
     * @returns The absolute value as a new Amount
     */
    abs() {
        let big = this.big.abs();
        return this.wrap(big);
    }
    /**
     * Compares this Amount with another value.
     *
     * @param n - The value to compare with
     *
     * @returns -1 if less than, 0 if equal, 1 if greater than
     */
    cmp(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.cmp(n);
        }
        else if (n instanceof Amount) {
            return this.big.cmp(n.big);
        }
        else if (n.toString) {
            return this.big.cmp(n.toString());
        }
        else {
            return this.big.cmp(+n);
        }
    }
    /**
     * Divides this Amount by another value.
     *
     * @param n - The divisor value
     *
     * @returns The division result as a new Amount
     */
    div(n) {
        this.checkNumberNotNull(n);
        let big;
        if (typeof n == "string") {
            big = this.big.div(n);
        }
        else if (n instanceof Amount) {
            big = this.big.div(n.big);
        }
        else if (n.toString) {
            big = this.big.div(n.toString());
        }
        else {
            big = this.big.div(+n);
        }
        return this.wrap(big);
    }
    /**
     * Checks if this Amount equals another value.
     *
     * @param n - The value to compare with
     *
     * @returns True if equal, false otherwise
     */
    eq(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.eq(n);
        }
        else if (n instanceof Amount) {
            return this.big.eq(n.big);
        }
        else if (n.toString) {
            return this.big.eq(n.toString());
        }
        else {
            return this.big.eq(+n);
        }
    }
    /**
     * Checks if this Amount is greater than another value.
     *
     * @param n - The value to compare with
     *
     * @returns True if greater than, false otherwise
     */
    gt(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.gt(n);
        }
        else if (n instanceof Amount) {
            return this.big.gt(n.big);
        }
        else if (n.toString) {
            return this.big.gt(n.toString());
        }
        else {
            return this.big.gt(+n);
        }
    }
    /**
     * Checks if this Amount is greater than or equal to another value.
     *
     * @param n - The value to compare with
     *
     * @returns True if greater than or equal, false otherwise
     */
    gte(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.gte(n);
        }
        else if (n instanceof Amount) {
            return this.big.gte(n.big);
        }
        else if (n.toString) {
            return this.big.gte(n.toString());
        }
        else {
            return this.big.gte(+n);
        }
    }
    /**
     * Checks if this Amount is less than another value.
     *
     * @param n - The value to compare with
     *
     * @returns True if less than, false otherwise
     */
    lt(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.lt(n);
        }
        else if (n instanceof Amount) {
            return this.big.lt(n.big);
        }
        else if (n.toString) {
            return this.big.lt(n.toString());
        }
        else {
            return this.big.lt(+n);
        }
    }
    /**
     * Checks if this Amount is less than or equal to another value.
     *
     * @param n - The value to compare with
     *
     * @returns True if less than or equal, false otherwise
     */
    lte(n) {
        this.checkNumberNotNull(n);
        if (typeof n == "string") {
            return this.big.lte(n);
        }
        else if (n instanceof Amount) {
            return this.big.lte(n.big);
        }
        else if (n.toString) {
            return this.big.lte(n.toString());
        }
        else {
            return this.big.lte(+n);
        }
    }
    /**
     * Adds another value to this Amount.
     *
     * @param n - The value to add
     *
     * @returns The sum as a new Amount
     */
    plus(n) {
        this.checkNumberNotNull(n);
        let big;
        if (typeof n == "string") {
            big = this.big.plus(n);
        }
        else if (n instanceof Amount) {
            big = this.big.plus(n.big);
        }
        else if (n.toString) {
            big = this.big.plus(n.toString());
        }
        else {
            big = this.big.plus(+n);
        }
        return this.wrap(big);
    }
    /**
     * Subtracts another value from this Amount.
     *
     * @param n - The value to subtract
     *
     * @returns The difference as a new Amount
     */
    minus(n) {
        this.checkNumberNotNull(n);
        let big;
        if (typeof n == "string") {
            big = this.big.minus(n);
        }
        else if (n instanceof Amount) {
            big = this.big.minus(n.big);
        }
        else if (n.toString) {
            big = this.big.minus(n.toString());
        }
        else {
            big = this.big.minus(+n);
        }
        return this.wrap(big);
    }
    /**
     * Calculates the modulo (remainder) of dividing this Amount by another value.
     *
     * Similar to % operator
     *
     * @param n - The divisor value
     *
     * @returns The remainder as a new Amount
     */
    mod(n) {
        this.checkNumberNotNull(n);
        let big;
        if (typeof n == "string") {
            big = this.big.mod(n);
        }
        else if (n instanceof Amount) {
            big = this.big.mod(n.big);
        }
        else if (n.toString) {
            big = this.big.mod(n.toString());
        }
        else {
            big = this.big.mod(+n);
        }
        return this.wrap(big);
    }
    /**
     * Rounds this Amount to a maximum of dp decimal places.
     *
     * @param dp - The number of decimal places (optional)
     *
     * @returns The rounded value as a new Amount
     */
    round(dp) {
        let big = this.big.round(dp);
        return this.wrap(big);
    }
    /**
     * Multiplies this Amount by another value.
     *
     * @param n - The value to multiply by
     *
     * @returns The product as a new Amount
     */
    times(n) {
        this.checkNumberNotNull(n);
        let big;
        if (typeof n == "string") {
            big = this.big.times(n);
        }
        else if (n instanceof Amount) {
            big = this.big.times(n.big);
        }
        else if (n.toString) {
            big = this.big.times(n.toString());
        }
        else {
            big = this.big.times(+n);
        }
        return this.wrap(big);
    }
    /**
     * Returns a string representing the value of this Amount in normal notation to a fixed number of decimal places.
     *
     * @param dp - The number of decimal places (optional)
     *
     * @returns The formatted string representation
     */
    toFixed(dp) {
        return this.big.toFixed(dp);
    }
    /**
     * Returns a string representing the value of this Amount.
     *
     * @returns The string representation of this Amount
     */
    toString() {
        return this.big.toString();
    }
    /**
     * Returns a primitive number representing the value of this Amount.
     *
     * @returns The numeric value of this Amount
     */
    toNumber() {
        return this.big.toNumber();
    }
    /** @internal */
    checkNumberNotNull(amount) {
        if (amount == null) {
            throw new Error(`Invalid number: null`);
        }
    }
    /** @internal */
    static create() {
        return Object.create(this.prototype);
    }
    /** @internal */
    wrap(big) {
        let amount = Amount.create();
        amount.big = big;
        return amount;
    }
}
//# sourceMappingURL=Amount.js.map